import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  Dialog,
  Box
} from '@mui/material';
import { DynamicFormInputs } from 'pages/Common';
import { DynamicInput } from 'pages/Common/model';

type CouponFormData = {
  id?: number;
  name?: string;
  code?: string;
  discountType?: 'percent' | 'fixed' | string;
  discountValue?: number;
  status?: number;
  validFrom?: string | Date | null;
  validUntil?: string | Date | null;
  maxRedemptions?: number;
  [key: string]: any;
};

type CouponDialogProps = {
  open: boolean;
  onClose: () => void;
  onSave: (data: CouponFormData) => void;
  coupon: CouponFormData | null;
};

export const CouponDialog = ({ open, onClose, onSave, coupon }: CouponDialogProps) => {
  const [data, setData] = useState<CouponFormData>(coupon || {});

  useEffect(() => {
    setData(coupon || {});
  }, [coupon, open]);

  const inputs: DynamicInput[] = useMemo(() => ([
    {
      field: 'name',
      label: 'Name',
      type: 'text',
      value: coupon?.name || ''
    },
    {
      field: 'code',
      label: 'Code',
      type: 'text',
      value: coupon?.code || ''
    },
    {
      field: 'discountType',
      label: 'Discount Type',
      type: 'select',
      options: [
        { label: 'Percentage', id: 'percent' },
        { label: 'Fixed Amount', id: 'fixed' }
      ],
      value: coupon?.discountType || 'percent'
    },
    {
      field: 'discountValue',
      label: 'Discount Value',
      type: 'number',
      value: coupon?.discountValue || 0
    },
    {
      field: 'active',
      label: 'Active',
      type: 'select',
      options: [
        { label: 'Yes', id: true },
        { label: 'No', id: false }
      ],
      value: coupon?.active
    },
    {
      field: 'validFrom',
      label: 'Valid From',
      type: 'date',
      value: coupon?.validFrom ? String(coupon.validFrom) : null
    },
    {
      field: 'validUntil',
      label: 'Valid Until',
      type: 'date',
      value: coupon?.validUntil ? String(coupon.validUntil) : null
    },
    {
      field: 'maxRedemptions',
      label: 'Max Redemptions',
      type: 'number',
      value: coupon?.maxRedemptions || 0
    },
  ]), [coupon]);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSave(data || {});
    onClose();
  }

  return (
    <Dialog onClose={onClose} maxWidth="sm" fullWidth open={open}>
      <form onSubmit={onSubmit}>
        <DialogTitle>{coupon ? 'Edit' : 'Add'} Coupon</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <DynamicFormInputs
              inputs={inputs}
              fullWidth={true}
              onChange={(value) => setData(value as CouponFormData)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary" autoFocus>
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
